import { IonSpinner, IonThumbnail } from '@ionic/react';
import { ReactNode, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { DuoClapperboardPlayIcon, DuoDocFileIcon, DuoFileIcon, DuoPDFFileIcon } from '../../../theme/Icons';
import { useResolvedMediaUrl } from '../../../media/useResolvedMediaUrl';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Href } from '../../../components/Href';
import { IAttachmentLink } from './IAttachment';
import { AttachmentOpener } from './AttachmentOpener';

const useStyles = createUseStyles({
    thumbnail: {
        width: 110,
        '--size': '70px',
        height: 70,
        '--border-radius': '5px',
        borderRadius: 5,
        border: '1px solid var(--ion-color-step-200)',
        backgroundColor: 'var(--ion-color-light)',
        //overflow: 'hidden',
        position: 'relative'
    },

    document: {
        padding: 6
    },

    iconWrapper: {
        marginRight: 4,
        display: 'inline-block'
    },

    thumbnailMask: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        opacity: 0.8,

        '& ion-spinner': {
            width: '70%',
            height: '90%'
        }
    },

    innerWrapper: {
        fontSize: 14,
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 3,
        // for non-webkit browsers (Firefox)
        minHeight: '100%',
        maxHeight: '3.8rem',
        lineHeight: 'initial'
    },

    videoIconWrapper: {
        display: 'flex',
        height: '100%',

        '& > ion-text': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
});

type INoteAttachmentProps = {
    url: string;
    fileName: string;
    isUploading?: boolean;
    contentType?: string;
    // Present when the API offered the authorised download route for this attachment.
    links?: { download?: IAttachmentLink };
};


type IAttachmentButton = INoteAttachmentProps & {
    children: ReactNode;
};

const NoteAttachmentButton: React.FC<IAttachmentButton> = ({ url, fileName, children, isUploading, links }) => {
    const classes = useStyles();
    const [downloading, setDownloading] = useState<boolean>(false);
    const opener = useInstance(AttachmentOpener);

    const open = () => opener.open({ url, fileName, href: links?.download?.href, onDownloading: setDownloading });

    if (isUploading) {
        return <>{children}</>;
    }

    return downloading ? (
        <>
            {children}
            <div className={classes.thumbnailMask}>
                <IonSpinner name="dots" color="primary" />
            </div>
        </>
    ) : (
        <Href onClick={open}>
            {children}
        </Href>
    );
};

export const NoteAttachment: React.FC<INoteAttachmentProps> = (props) => {
    const classes = useStyles();

    const {fileName, isUploading = false, contentType} = props;
    const resolvedUrl = useResolvedMediaUrl({ url: props.url, href: props.links?.download?.href });

    const getCompForContentType = () => {
        const mimeType = contentType?.split('/')[1] || '';
        const isImage: boolean = contentType?.startsWith('image/') || false;
        const isVideo: boolean = contentType?.startsWith('video/') || false;

        const fileIcons: any = {
            'pdf': <DuoPDFFileIcon color="danger" />,
            'msword': <DuoDocFileIcon color="google-docs" />,
            'vnd.openxmlformats-officedocument.wordprocessingml.document': <DuoDocFileIcon />
        };

        return isImage && !isUploading ? (
            <IonThumbnail className={classes.thumbnail}>
                <img src={resolvedUrl} alt="attachment" className={classes.thumbnail} />
            </IonThumbnail>
        ) : isVideo && !isUploading ? (
            <IonThumbnail className={classes.thumbnail}>
                <div className={classes.videoIconWrapper}>
                    <DuoClapperboardPlayIcon customSize={42} />
                </div>
            </IonThumbnail>
        ) : (
            <div className={`${classes.thumbnail} ${classes.document}`}>
                <div className={classes.innerWrapper}>
                    <div className={classes.iconWrapper}>
                        {fileIcons[mimeType] || <DuoFileIcon />}
                    </div>
                    {fileName.substring(0, fileName.lastIndexOf('.')) || fileName}
                </div>
                {isUploading && (
                    <div className={classes.thumbnailMask}>
                        <IonSpinner name="dots" color="primary" />
                    </div>
                )}
            </div>
        );
    };

    const dataProps = {
        'data-type': isUploading ?  'uploading-file': 'attachment-button'
    };

    return (
        <div {...dataProps} style={{position: 'relative'}}>
            <NoteAttachmentButton {...props}>
                {getCompForContentType()}
            </NoteAttachmentButton>
        </div>

    );
};
